import type React from "react";

import type { LanguageDto } from "@/api/types";
import { PencilLineIcon } from "@/components/Icons/PencilLineIcon";
import { Translate01Icon } from "@/components/Icons/Translate01Icon";
import { Box } from "@/design-system/Box";
import { Button } from "@/design-system/Button";
import { Card } from "@/design-system/Card";
import { Inline } from "@/design-system/Inline";
import { Stack } from "@/design-system/Stack";
import { Switch } from "@/design-system/Switch";
import { getFlagIcon } from "@/helpers/flags";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { useTranslation } from "@/translations";

import { AutoTranslationLanguageSettingModal } from "./AutoTranslationLanguageSettingModal";

interface AutoTranslationLanguageSelectorProps {
  languages: LanguageDto[];
  selectedLanguage: LanguageDto | null;
  onSelectLanguage: (language: LanguageDto | null) => void;
}

export function AutoTranslationLanguageSelector({
  languages,
  selectedLanguage,
  onSelectLanguage,
}: AutoTranslationLanguageSelectorProps): React.ReactNode {
  const [isAutoTranslationSettingModalOpened, autoTranslationSettingModalHandler] = useBool(false);

  const sessionUser = useSessionUser();
  const { t } = useTranslation();

  const handleEnable = () => {
    onSelectLanguage(sessionUser.language);
  };

  const handleDisable = () => {
    onSelectLanguage(null);
  };

  return (
    <>
      <Card as="section" density="flush" borderTone="secondary">
        <Box padding="md">
          <Stack gap="sm">
            <Inline gap="sm" align="center">
              <Translate01Icon size={24} />
              <span className="text-body-emphasis">
                {t("page.create-user.form.auto-translation-language.switch.label")}
              </span>
              <span className="ml-auto">
                <Switch
                  isChecked={Boolean(selectedLanguage)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      handleEnable();
                    } else {
                      handleDisable();
                    }
                  }}
                  ariaLabel={t("page.create-user.form.auto-translation-language.switch.label")}
                />
              </span>
            </Inline>
            <p className="text-body">{t("page.create-user.form.auto-translation-language.switch.description")}</p>
          </Stack>
        </Box>
        {selectedLanguage && (
          <section className="flex flex-col gap-space-sm border-t border-t-border-secondary p-space-md">
            <Inline gap="sm" align="center">
              {getFlagIcon({
                languageId: selectedLanguage.id,
                width: 24,
              })}
              <span data-testid={"selected-automatic-translation-language"} className="text-body-emphasis">
                {selectedLanguage.description}
              </span>
              <span className="ml-auto w-fit">
                <Button
                  data-testid="edit-btn"
                  icon={<PencilLineIcon />}
                  onClick={() => autoTranslationSettingModalHandler.setTrue()}
                  variant="ghostPrimary"
                >
                  {t("common.action.edit")}
                </Button>
              </span>
            </Inline>
            <p className="text-caption text-text-secondary">{selectedLanguage.previewText}</p>
          </section>
        )}
      </Card>

      {selectedLanguage && (
        <AutoTranslationLanguageSettingModal
          languages={languages}
          selectedLanguage={selectedLanguage}
          onSelectLanguage={onSelectLanguage}
          isOpened={isAutoTranslationSettingModalOpened}
          onOpenChange={autoTranslationSettingModalHandler.set}
        />
      )}
    </>
  );
}
